/**
 * Utility functions for calculating section start times from timestamps
 */

/**
 * Calculate the earliest timestamp from a timestamps object
 * Returns ISO string of earliest timestamp, or null if no timestamps exist
 */
export const calculateSectionStartTime = (
  timestamps: Record<string, string | undefined> | undefined
): string | null => {
  if (!timestamps) return null;

  const validTimestamps = Object.values(timestamps)
    .filter((ts): ts is string => ts !== undefined && ts !== null)
    .map(ts => new Date(ts).getTime())
    .filter(time => !isNaN(time));

  if (validTimestamps.length === 0) return null;

  const earliest = Math.min(...validTimestamps);
  return new Date(earliest).toISOString();
};

/**
 * Format ISO timestamp to Israeli format (DD/MM/YYYY HH:MM)
 * Returns formatted string or empty string if timestamp is null
 */
export const formatTimestampIL = (timestamp: string | number | null | undefined): string => {
  if (!timestamp) return '';

  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format date to short Israeli format (DD/MM HH:MM) - no year
 * Used for compact display in PDF
 */
export const formatTimestampShort = (timestamp: string | number | null | undefined): string => {
  if (!timestamp) return '';

  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month} ${hours}:${minutes}`;
};
